import moment from 'moment-timezone'
import { useSelector } from 'react-redux'

import { dateNotationSelector, timezoneSelector } from 'selectors/current_user'
import { userDateFormat, userDateRangeFormat } from 'services/time_format'

import {
  DATE_WITH_TIME_ZONE_FORMAT,
  DATE_RANGES_WITH_UNITS,
  DEFAULT_DATE_RANGE,
  ROUTING_DATE_RANGES_WITH_UNITS,
  ROUTING_DATE_RANGE_PERIODS
} from './constants'

/**
 * @param {string|number} startDate - date string or unix timestamp in milliseconds
 * @param {string|number} endDate - date string or unix timestamp in milliseconds
 * @param {string} timezone - user's time zone
 * @param {string} dateNotation - user's date notation
 *
 * @returns {string} - formatted string with month and day (e.g. Feb 1–Mar 1).
 * Will include year if dates are not within the same year (e.g. Dec 31, 2020–Jan 1, 2021)
 * Will include year at end if dates are not the current year (e.g. Jun 13–Jun 23, 2020)
 */
export const getPrettyDateRange = (startDate, endDate, timezone, dateNotation) => {
  const startDateMoment = moment.tz(startDate, timezone)
  const endDateMoment = moment.tz(endDate, timezone)

  if (startDateMoment.isSame(endDateMoment, 'day')) {
    return userDateFormat(startDateMoment, dateNotation, 'date_small')
  }

  const fullFormat = startDateMoment.year() === moment.tz(timezone).year()
    ? 'date_small'
    : 'date_small_with_year'

  const [fromStr, toStr] = userDateRangeFormat(
    startDateMoment, endDateMoment, dateNotation, 'date_small', fullFormat,
  )
  return `${fromStr} – ${toStr}`
}

export const usePrettyDateRange = (startDate, endDate) => {
  const dateNotation = useSelector(dateNotationSelector)
  const timezone = useSelector(timezoneSelector)

  if (!startDate || !endDate) return ''

  return getPrettyDateRange(startDate, endDate, timezone, dateNotation)
}

/**
 * @param {string} date - date string
 * @param {string} timezone - user's time zone
 *
 * @returns {string} - formatted string with month and year (e.g. Aug 2023).
 */
export const getPrettyMonthDate = (date, timezone) => (
  moment(date).tz(timezone).format('MMM, YYYY')
)

/**
 * @param {string} start - date string
 * @param {string} end - date string
 *
 * @returns {string} - string in set day, week, or month for reasonable granularity
 */
export const getStandardGranularity = (start, end) => {
  const days = moment(end).diff(moment(start), 'days')
  if (days < 31) {
    return 'day'
  }
  if (days > 365) {
    return 'month'
  }
  return 'week'
}

/**
 * @param {number} number
 * @param {boolean} [useStandardNotation]
 *
 * @returns {string} - formatted string display of the number
 */
export const formatNumber = (number, useStandardNotation = false) => (typeof number === 'number' ? (new Intl.NumberFormat(undefined, {
  notation: useStandardNotation ? 'standard' : 'compact',
  compactDisplay: 'short'
})).format(number) : number)

/**
 * @param {number} seconds - duration in seconds
 *
 * @returns {string} - duration in minutes with min abbreviation
 */
export const formatDurationInMinutes = (seconds) => (
  new Intl.NumberFormat(undefined, {
    style: 'unit', unit: 'minute', unitDisplay: 'short'
  }).format(Math.round(seconds / 60))
)

/**
 * @param {number} seconds - duration in seconds
 *
 * @returns {string} - duration in hours and seconds with h/m abbreviations when applicable
 */
export const formatDurationInHoursAndMinutes = (seconds, excludeZeroMinutes = false) => {
  if (seconds === 0) {
    return new Intl.NumberFormat(undefined, {
      style: 'unit', unit: 'minute', unitDisplay: 'narrow'
    }).format(seconds)
  }


  if (seconds < 60) {
    return new Intl.NumberFormat(undefined, {
      style: 'unit', unit: 'second', unitDisplay: 'narrow', minimumIntegerDigits: 2
    }).format(seconds)
  }

  const rawHours = Math.floor(seconds / 60 / 60)
  const rawMinutes = (seconds / 60) % 60

  const hours = new Intl.NumberFormat(undefined, {
    style: 'unit', unit: 'hour', unitDisplay: 'narrow'
  }).format(rawHours)

  if (excludeZeroMinutes && rawMinutes === 0) {
    return hours
  }

  const minutes = new Intl.NumberFormat(undefined, {
    style: 'unit', unit: 'minute', unitDisplay: 'narrow', maximumFractionDigits: 0, minimumIntegerDigits: 2
  }).format(rawMinutes)

  if (rawHours === 0) {
    return minutes
  }

  return `${hours}, ${minutes}`
}

/**
 * @param {string} start - date string
 * @param {string} end - date string
 * @param {string} timezone
 *
 * @returns {Object} - object with start and end in unix timestamp format
 */
export const formatDateParams = ({ start, end, timezone }) => ({
  start: moment.tz(start, timezone).unix(),
  end: moment.tz(end, timezone).unix()
})

/**
 * @param {string} start - date string
 * @param {string} end - date string
 * @param {string} timezone
 *
 * @returns {Object} params
 * @returns {string} params.start - date string
 * @returns {string} params.end - date string
 */
export const getLastPeriodDateParams = ({ start, end, timezone }) => {
  const startDate = moment.tz(start, timezone)
  const endDate = moment.tz(end, timezone)
  const deltaDays = Math.abs(startDate.diff(endDate, 'days')) + 1
  const deltaStartDate = moment.tz(start, timezone).subtract(deltaDays, 'days')

  return {
    start: deltaStartDate.unix(),
    end: startDate.unix()
  }
}

/**
 * @param {string} timezone
 * @param {number} offsetFromNowInDays
 *
 * @returns {Object} past - past times
 * @returns {string} past.start - date string
 * @returns {string} past.end - date string
 */
export const getPastTimes = (timezone, offsetFromNowInDays = 1, now = moment.tz(timezone)) => {
  const utcOffset = now.utcOffset()

  return {
    start: moment(now)
      .subtract(offsetFromNowInDays - 1, 'days')
      .startOf('day')
      .utcOffset(utcOffset)
      .format(DATE_WITH_TIME_ZONE_FORMAT),
    end: now.startOf('hour').format(DATE_WITH_TIME_ZONE_FORMAT)
  }
}

/**
 * @param {string} timezone
 * @param {number} offsetFromNowInDays
 *
 * @returns {Object} future - future times
 * @returns {string} future.start - date string
 * @returns {string} future.end - date string
 */
const getFutureTimes = (timezone, offsetFromNowInDays = 1, now = moment.tz(timezone)) => {
  const utcOffset = now.utcOffset()

  return {
    start: moment(now)
      .endOf('hour')
      .utcOffset(utcOffset)
      .format(DATE_WITH_TIME_ZONE_FORMAT),
    end: moment(now)
      .add(offsetFromNowInDays - 1, 'days')
      .endOf('day')
      .utcOffset(utcOffset)
      .format(DATE_WITH_TIME_ZONE_FORMAT),
  }
}

/**
 * @param {string} timezone
 * @param {number} offsetInDays
 *
 * @returns {Object} times - past and future start/end date times
 * @returns {Object} times.past - default past times
 * @returns {string} times.past.start - date string
 * @returns {string} times.past.end - date string
 * @returns {Object} times.future - default future times
 * @returns {string} times.future.start - date string
 * @returns {string} times.future.end - date string
 */
const getTimesForDays = (timezone, offsetInDays = 1) => {
  const now = moment.tz(timezone)
  return {
    past: getPastTimes(timezone, offsetInDays, now),
    future: getFutureTimes(timezone, offsetInDays, now)
  }
}

/**
 * @param {string} timezone
 *
 * @returns {Object} times - default past and future start/end date times
 * @returns {Object} times.past - default past times
 * @returns {string} times.past.start - date string
 * @returns {string} times.past.end - date string
 * @returns {Object} times.future - default future times
 * @returns {string} times.future.start - date string
 * @returns {string} times.future.end - date string
 */
export const getDefaultTimes = (timezone) => getTimesForDays(timezone, DEFAULT_DATE_RANGE)

/**
 * @param {number} timestamp
 * @param {string} timezone
 *
 * @returns {string} formatted timestamp
 */
export const formatTimestampAsString = (timestamp, timezone) => (
  timestamp && moment.unix(timestamp).tz(timezone).format(DATE_WITH_TIME_ZONE_FORMAT)
)

/**
 * @param {number} x
 * @param {number} y
 * @param {number} fractionDigits
 *
 * @returns {string} X percent of Y
 */
export const isWhatPercentOf = (x, y, fractionDigits = 1) => ((x / y) * 100).toFixed(fractionDigits)

/**
 * @param {number} value
 * @param {number} maxValue
 *
 * @returns {string} formatted percent
 */
export const formatAsPercent = (value, maxValue) => new Intl.NumberFormat(undefined, {
  style: 'unit',
  unit: 'percent',
  maximumFractionDigits: 1
}).format(maxValue > 0 ? isWhatPercentOf(value, maxValue) : 100)

/**
 * @param {string} range
 * @param {string} timezone
 *
 * @returns {array} date_array
 * @returns {string} date_array[0] formatted start timestamp
 * @returns {string} date_array[1] formatted end timestamp
 */
export const getSelectedDateRanges = (range, timezone, now = new Date()) => {
  const rangeKey = range?.toUpperCase()
  const unit = DATE_RANGES_WITH_UNITS[rangeKey] || 'week'
  const startOffset = Object.keys(DATE_RANGES_WITH_UNITS)
    .filter((key) => key.includes('NEXT'))
    .includes(rangeKey) ? 1 : 0

  const today = now ? moment(now).tz(timezone) : moment.tz(timezone)
  // force moment to use sunday as start of the week
  const start = today.add(startOffset, unit).locale('en-US').startOf(unit)
  const end = start.clone().endOf(unit)
  return [
    start.format(DATE_WITH_TIME_ZONE_FORMAT),
    end.format(DATE_WITH_TIME_ZONE_FORMAT)
  ]
}

/**
 * @param {string} range
 * @param {string} timezone
 *
 * @returns {array} date_array
 * @returns {string} date_array[0] formatted start timestamp
 * @returns {string} date_array[1] formatted end timestamp
 */
export const getSelectedRoutingDateRanges = (range, timezone, now = new Date()) => {
  const rangeKey = range?.toUpperCase()
  const unit = ROUTING_DATE_RANGES_WITH_UNITS[rangeKey] || ROUTING_DATE_RANGE_PERIODS.ALL_TIME
  // force moment to use sunday as start of the week
  const start = moment(now).tz(timezone).locale('en-US').startOf(unit)
  const end = Object.keys(ROUTING_DATE_RANGES_WITH_UNITS)
    .filter((key) => key.includes('THIS'))
    .includes(rangeKey) ? moment(now).tz(timezone).endOf('day') : start.clone().endOf(unit)

  return [
    start.format(DATE_WITH_TIME_ZONE_FORMAT),
    end.format(DATE_WITH_TIME_ZONE_FORMAT)
  ]
}
